import React, { useEffect, useRef } from 'react';
import { Image, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { auth0Audience, Auth0Metadata } from '../api/API';
import { Environment } from '../Environment';
import { notificationSlice } from '../model/notificationSlice';
import { RootState } from '../model/store';
import { studentSlice } from '../model/studentSlice';
import { colors, fonts, scale } from '../Styles';
import { history } from '../util/History';
import { Auth0Context, useAuth0 } from './Auth0Provider';
import { Role } from './PrivateRoute';

type Props = {
  logout: () => void;
};

export const DrawerContents: React.FC<Props> = ({ logout }) => {
  /** A simple function whose main purpose is to increase readability. */
  const navigateTo = (route: string) => history.push(route);

  const { api } = Environment.current();
  const { isAuthenticated, getTokenSilently, user } = useAuth0() as Auth0Context;

  // Namespace is student, but this includes the admin
  const student = useSelector((state: RootState) => state.student.profile);
  const notifications = useSelector((state: RootState) => state.notification.list);
  const dispatch = useDispatch();

  const personId = user?.[Auth0Metadata.PersonId];
  const isMounted = useRef(true);

  // To prevent updates on unmount
  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // Get student profile
  useEffect(() => {
    (async () => {
      if (isAuthenticated && personId && !student) {
        try {
          const token = await getTokenSilently({ audience: auth0Audience });
          const profile = await api.getStudentById(personId, token || '');
          if (isMounted.current) {
            dispatch(studentSlice.actions.receivedStudent({ student: profile }));
          }
        } catch (e) {
          toast.error(`${e.message} (Drawer)`);
        }
      }
    })();
  }, [isAuthenticated, personId, student]);

  // Get notifications
  useEffect(() => {
    (async () => {
      if (isAuthenticated && personId && !notifications) {
        try {
          const token = await getTokenSilently({ audience: auth0Audience });
          const list = await api.getStudentNotifications(personId, token || '');
          if (isMounted.current) {
            dispatch(notificationSlice.actions.receivedList({ list }));
          }
        } catch (e) {
          toast.error(`${e.message} (Drawer)`);
        }
      }
    })();
  }, [isAuthenticated, personId, notifications]);

  const routes: {
    key: string;
    allowedRoles: Role[];
    onPress: () => void;
    label: string | (() => React.ReactNode);
  }[] = [
    {
      key: 'notifications',
      onPress: () => navigateTo('/notifications'),
      label: () => <Text style={styles.menuText}>Notifications</Text>,
      allowedRoles: [Role.Admin, Role.Student],
    },
    {
      key: 'overall-student-list',
      onPress: () => navigateTo('/overall-student-list'),
      label: () => <Text style={styles.menuText}>Overall Student List</Text>,
      allowedRoles: [Role.Admin],
    },
    {
      key: 'transcript-requests',
      onPress: () => navigateTo('/transcript-requests'),
      label: () => <Text style={styles.menuText}>Transcript Requests</Text>,
      allowedRoles: [Role.Admin],
    },
    {
      key: 'upload-students',
      onPress: () => navigateTo('/upload-students'),
      label: () => <Text style={styles.menuText}>Upload Students</Text>,
      allowedRoles: [Role.Admin],
    },
    {
      key: 'upload-student-results',
      onPress: () => navigateTo('/upload-student-results'),
      label: () => <Text style={styles.menuText}>Upload Student Results</Text>,
      allowedRoles: [Role.Admin],
    },
    {
      key: 'profile',
      onPress: () => navigateTo('/profile'),
      label: () => <Text style={styles.menuText}>Profile</Text>,
      allowedRoles: [Role.Student],
    },
    {
      key: 'academic-overview',
      onPress: () => navigateTo('/academic-overview'),
      label: () => (
        <View>
          <Text style={[styles.menuText, styles.underlined]}>Academic Check</Text>
          <Text style={styles.academicCheckSubs}>
            Academic Check shows your progress towards graduation: credits you have completed and what you have left to
            do.
          </Text>
        </View>
      ),
      allowedRoles: [Role.Student],
    },
    {
      key: 'request-transcript',
      onPress: () => navigateTo('/request-transcript'),
      label: () => <Text style={styles.menuText}>Request Transcript</Text>,
      allowedRoles: [Role.Student],
    },
  ];

  return (
    <Auth0Context.Consumer>
      {context => {
        // Milos suggested that the default role should be Student
        const roles = context?.user?.[Auth0Metadata.Roles]?.length ? context.user[Auth0Metadata.Roles] : [Role.Student];
        const isAdmin = Boolean([Role.Admin].find(allowedRole => roles.includes(allowedRole)));

        return (
          <ScrollView style={styles.wrapper}>
            <View style={styles.headerWrapper}>
              <Text style={styles.titleText}>{isAdmin ? 'ADMIN' : 'MNOHS Talent Squad'}</Text>
              <Image
                source={{ uri: student?.imageBase64 ?? context?.user?.picture }}
                style={styles.avatar}
                resizeMode="cover"
              />
              <Text style={styles.headerText}>
                {isAdmin
                  ? // ADMIN
                    context?.user?.nickname
                  : // STUDENT
                  student?.firstName && student?.lastName
                  ? `${student.firstName} ${student.lastName}`
                  : context?.user?.nickname}
              </Text>
            </View>

            <View style={styles.drawerContentWrapper}>
              {routes.map(({ key, allowedRoles, onPress, label: Label }) => {
                const pathname = window.location.pathname.split('/')[1];
                if (allowedRoles.find(allowedRole => roles.includes(allowedRole))) {
                  return (
                    <TouchableOpacity
                      key={key}
                      style={[styles.drawerItem, pathname === key && { backgroundColor: colors.grayLight }]}
                      onPress={onPress}>
                      <Label />
                    </TouchableOpacity>
                  );
                } else {
                  return null;
                }
              })}

              <TouchableOpacity style={styles.drawerItem} onPress={logout}>
                <Text style={styles.menuText}>Log out</Text>
              </TouchableOpacity>
            </View>
          </ScrollView>
        );
      }}
    </Auth0Context.Consumer>
  );
};

const styles = StyleSheet.create({
  avatar: {
    width: scale(100),
    height: scale(100),
    borderRadius: scale(50),
    marginTop: scale(14),
    marginBottom: scale(10),
  },
  drawerItem: {
    width: '100%',
    borderRadius: scale(4),
    paddingVertical: scale(13),
    paddingHorizontal: scale(8),
    marginVertical: scale(4),
  },
  drawerContentWrapper: {
    alignItems: 'center',
    width: '100%',
    padding: scale(20),
  },
  titleText: {
    ...fonts.descriptionText,
    color: colors.black,
    textAlign: 'center',
    textTransform: 'uppercase',
    fontWeight: 'bold',
  },
  headerText: {
    ...fonts.header2,
    textAlign: 'center',
    marginTop: scale(16),
  },
  headerWrapper: {
    alignItems: 'center',
    backgroundColor: colors.grayLight,
    width: '100%',
    paddingVertical: scale(32),
    paddingHorizontal: scale(20),
  },
  logoutContainer: {
    paddingLeft: '5%',
    paddingVertical: '8%',
    borderBottomColor: 'transparent',
    borderBottomWidth: scale(10),
    width: '100%',
  },
  menuText: {
    ...fonts.linkedText,
    fontWeight: 'bold',
  },
  underlined: {
    paddingBottom: scale(8),
    borderBottomWidth: scale(2),
    borderBottomColor: colors.black,
  },
  wrapper: {
    // alignItems: 'center',
    height: '100%',
    width: '100%',
  },
  academicCheckSubs: {
    ...fonts.descriptionText,
    marginTop: scale(8),
  },
  notification: {
    marginTop: scale(16),
    width: scale(45),
    height: scale(45),
  },
});
